<template>
  <section class="bg-creme-200 pt-0 pb-12 sm:pb-16 px-4">
    <div class="max-w-7xl mx-auto pt-6 sm:pt-0">
      <div class="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-center mb-8 sm:mb-12 relative">
        <h2 class="text-3xl xl:text-4xl font-header font-bold text-primary text-left sm:text-center">
          Recommended Reads
        </h2>
        <a v-if="displayedRecommendedReads.length > 0" href="/books?category=recommended-reads"
          class="text-primary hover:text-accent underline uppercase transition-colors font-body text-sm sm:text-base mt-1 sm:mt-0 sm:absolute sm:right-0">
          See All
        </a>
      </div>
      <div v-if="displayedRecommendedReads.length > 0" class="relative flex">
        <button @click="scrollLeft" :disabled="!canScrollLeft"
          class="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 backdrop-blur-sm rounded-full p-2 md:p-3 shadow-lg hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Scroll left">
          <svg class="w-5 h-5 md:w-6 md:h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div ref="sliderContainer" class="overflow-x-auto scrollbar-hide scroll-smooth max-w-fit"
          @scroll="updateScrollState">
          <div class="flex gap-6 pb-4">
            <BookCard v-for="book in displayedRecommendedReads" :key="book.id" :book="book" />
          </div>
        </div>

        <button @click="scrollRight" :disabled="!canScrollRight"
          class="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 backdrop-blur-sm rounded-full p-2 md:p-3 shadow-lg hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Scroll right">
          <svg class="w-5 h-5 md:w-6 md:h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      <div v-else class="text-center py-12">
        <p class="text-lg text-gray-600 font-body">{{ emptyMessage }}</p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getRecommendedReads } from '../utils/books'
import BookCard from './BookCard.vue'
import { useEmptyMessages } from '../composables/useEmptyMessages'

const { getEmptyMessage } = useEmptyMessages()
const emptyMessage = computed(() => getEmptyMessage('recommended-reads') || 'No books found for Recommended Reads.')

const sliderContainer = ref(null)
const canScrollLeft = ref(false)
const canScrollRight = ref(true)

const updateScrollState = () => {
  if (!sliderContainer.value) return
  const { scrollLeft, scrollWidth, clientWidth } = sliderContainer.value
  canScrollLeft.value = scrollLeft > 0
  canScrollRight.value = scrollLeft < scrollWidth - clientWidth - 10
}

const scrollLeft = () => {
  if (!sliderContainer.value) return
  const scrollAmount = sliderContainer.value.clientWidth * 0.8
  sliderContainer.value.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
}

const scrollRight = () => {
  if (!sliderContainer.value) return
  const scrollAmount = sliderContainer.value.clientWidth * 0.8
  sliderContainer.value.scrollBy({ left: scrollAmount, behavior: 'smooth' })
}

const recommendedReads = ref([])

onMounted(async () => {
  recommendedReads.value = await getRecommendedReads()
  updateScrollState()
  if (sliderContainer.value) {
    sliderContainer.value.addEventListener('scroll', updateScrollState)
  }
})

const displayedRecommendedReads = computed(() => recommendedReads.value.slice(0, 8))
</script>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
  overscroll-behavior: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
